import React, { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Grid } from '@mui/material';
import InputText from '@/components/UI/InputText/InputText';
import ExternalReworkTimes from '@/containers/SubCampaignDetail/Feedback/ExternalReworkTimes';
import { FEEDBACK } from '@/helpers/constants';
import { getIsVANSubCR, getSubCampaignFeedback } from '@/redux/SubCampaign/subCampaign.reducer';
import { SubCampaignFeedback } from '@/types';
import { formatValue, getTotalReworkTime } from './feedback-utils';

const INTERNAL_TOTAL_FIELD = 'internalReworkTimes.total' as const;
const INTERNAL_SHUNTING_ALWAYS_FIELD = 'internalReworkTimes.shuntingAlways' as const;
const INTERNAL_SHUNTING_REWORK_FIELD = 'internalReworkTimes.shuntingWithRework' as const;
const INTERNAL_INSPECTION_FIELD = 'internalReworkTimes.inspection' as const;
const INTERNAL_REWORK_FIELD = 'internalReworkTimes.rework' as const;
const INTERNAL_DIAGNOSIS_FIELD = 'internalReworkTimes.diagnosis' as const;
const INTERNAL_ROLLER_TEST_FIELD = 'internalReworkTimes.rollerTest' as const;
const BILLING_COST_CENTER_FIELD = 'billing.costCenter' as const;
const BILLING_PM_CONTRACT_FIELD = 'billing.pmContract' as const;
const BILLING_CO_NUMBER_FIELD = 'billing.coNumber' as const;

interface TimeBillingSectionProps {
  subCampaignFeedback: SubCampaignFeedback;
  handleChangeIntegerField: (label: string, value: string, section: string, isSyncField: boolean, index: number, isCalculated?: boolean) => void;
  handleChangeText: (label: string, value: string, section: string) => void;
  checkIsDisabledField: (field: string) => boolean;
  getLabelForPossibleDisabledInput: (label: string, field: string) => ReactNode;
  index: number;
  isVANSubCR: boolean;
}

const TimeBillingSection: React.FC<TimeBillingSectionProps> = (props) => {
  const { subCampaignFeedback, handleChangeIntegerField, handleChangeText, checkIsDisabledField,
    getLabelForPossibleDisabledInput, index, isVANSubCR } = props;

  const { t } = useTranslation();
  const campaign = subCampaignFeedback.campaigns[index];
  const { shuntingAlways, shuntingWithRework, inspection, rework, diagnosis, rollerTest, total } = campaign.internalReworkTimes || {};

  useEffect(() => {
    handleChangeIntegerField(
      FEEDBACK.TOTAL,
      `${getTotalReworkTime([shuntingAlways, shuntingWithRework, inspection, rework, diagnosis, rollerTest])}`,
      FEEDBACK.INT_REWORK,
      false,
      index,
      true,
    );
  }, [shuntingAlways, shuntingWithRework, inspection, rework, diagnosis, rollerTest]);

  return (
    <>
      <div className="w-100" data-testid={`feedback-internal-time-section-${index}`}>
        <h3>{t('feedback.internalReworkTimes')}</h3>
        <Grid container spacing={2} rowSpacing={2} sx={{ marginBottom: '16px' }} columnSpacing={1}>
          <Grid item xs={12} sm={6} md={3}>
            <InputText
              id={`input-campaign-totalTime-feedback-${index}`}
              fieldPath={[FEEDBACK.FEEDBACK, `campaigns[${index}]`, FEEDBACK.INT_REWORK, FEEDBACK.TOTAL]}
              noTranslationField
              label={getLabelForPossibleDisabledInput('feedback.internalTotalTime', INTERNAL_TOTAL_FIELD)}
              type="number"
              value={formatValue(total)}
              className="w-100"
              disabled
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InputText
              id={`input-campaign-timeWithoutRework-feedback-${index}`}
              fieldPath={[FEEDBACK.FEEDBACK, `campaigns[${index}]`, FEEDBACK.INT_REWORK, FEEDBACK.SHUNTING_ALWAYS]}
              noTranslationField
              label={getLabelForPossibleDisabledInput('feedback.timeWithoutRework', INTERNAL_SHUNTING_ALWAYS_FIELD)}
              type="number"
              handleChange={(ev) => handleChangeIntegerField(FEEDBACK.SHUNTING_ALWAYS, ev.target.value, FEEDBACK.INT_REWORK, false, index)}
              placeholder={`${t('feedback.message')} (min: 1, max: 9999)`}
              value={formatValue(shuntingAlways)}
              className="w-100"
              disabled={checkIsDisabledField(INTERNAL_SHUNTING_ALWAYS_FIELD)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InputText
              id={`input-campaign-timeWithRework-feedback-${index}`}
              fieldPath={[FEEDBACK.FEEDBACK, `campaigns[${index}]`, FEEDBACK.INT_REWORK, FEEDBACK.SHUNTING_REWORK]}
              noTranslationField
              label={getLabelForPossibleDisabledInput('feedback.timeWithRework', INTERNAL_SHUNTING_REWORK_FIELD)}
              type="number"
              handleChange={(ev) => handleChangeIntegerField(FEEDBACK.SHUNTING_REWORK, ev.target.value, FEEDBACK.INT_REWORK, false, index)}
              placeholder={`${t('feedback.message')} (min: 1, max: 9999)`}
              value={formatValue(shuntingWithRework)}
              className="w-100"
              disabled={checkIsDisabledField(INTERNAL_SHUNTING_REWORK_FIELD)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InputText
              id={`input-campaign-checkTime-feedback-${index}`}
              fieldPath={[FEEDBACK.FEEDBACK, `campaigns[${index}]`, FEEDBACK.INT_REWORK, FEEDBACK.INSPECTION]}
              noTranslationField
              label={getLabelForPossibleDisabledInput('feedback.checkTime', INTERNAL_INSPECTION_FIELD)}
              type="number"
              handleChange={(ev) => handleChangeIntegerField(FEEDBACK.INSPECTION, ev.target.value, FEEDBACK.INT_REWORK, false, index)}
              placeholder={`${t('feedback.message')} (min: 1, max: 9999)`}
              value={formatValue(inspection)}
              className="w-100"
              disabled={checkIsDisabledField(INTERNAL_INSPECTION_FIELD)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InputText
              id={`input-campaign-reworkTime-feedback-${index}`}
              fieldPath={[FEEDBACK.FEEDBACK, `campaigns[${index}]`, FEEDBACK.INT_REWORK, FEEDBACK.REWORK]}
              noTranslationField
              label={getLabelForPossibleDisabledInput('feedback.reworkTime', INTERNAL_REWORK_FIELD)}
              type="number"
              handleChange={(ev) => handleChangeIntegerField(FEEDBACK.REWORK, ev.target.value, FEEDBACK.INT_REWORK, false, index)}
              placeholder={`${t('feedback.message')} (min: 1, max: 9999)`}
              value={formatValue(rework)}
              className="w-100"
              disabled={checkIsDisabledField(INTERNAL_REWORK_FIELD)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InputText
              id={`input-campaign-diagnosisTime-feedback-${index}`}
              fieldPath={[FEEDBACK.FEEDBACK, `campaigns[${index}]`, FEEDBACK.INT_REWORK, FEEDBACK.DIAGNOSIS]}
              noTranslationField
              label={getLabelForPossibleDisabledInput('feedback.diagnosisTime', INTERNAL_DIAGNOSIS_FIELD)}
              type="number"
              handleChange={(ev) => handleChangeIntegerField(FEEDBACK.DIAGNOSIS, ev.target.value, FEEDBACK.INT_REWORK, false, index)}
              placeholder={`${t('feedback.message')} (min: 1, max: 9999)`}
              value={formatValue(diagnosis)}
              className="w-100"
              disabled={checkIsDisabledField(INTERNAL_DIAGNOSIS_FIELD)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InputText
              id={`input-campaign-rollerTime-feedback-${index}`}
              fieldPath={[FEEDBACK.FEEDBACK, `campaigns[${index}]`, FEEDBACK.INT_REWORK, FEEDBACK.ROLLER_TEST]}
              noTranslationField
              label={getLabelForPossibleDisabledInput('feedback.rollerTime', INTERNAL_ROLLER_TEST_FIELD)}
              type="number"
              handleChange={(ev) => handleChangeIntegerField(FEEDBACK.ROLLER_TEST, ev.target.value, FEEDBACK.INT_REWORK, false, index)}
              placeholder={`${t('feedback.message')} (min: 1, max: 9999)`}
              value={formatValue(rollerTest)}
              className="w-100"
              disabled={checkIsDisabledField(INTERNAL_ROLLER_TEST_FIELD)}
            />
          </Grid>
        </Grid>
      </div>

      <div className="w-100" data-testid={`feedback-external-time-section-${index}`}>
        <ExternalReworkTimes
          index={index}
          campaign={campaign}
          getLabelForPossibleDisabledInput={getLabelForPossibleDisabledInput}
          checkIsDisabledField={checkIsDisabledField}
          handleChangeIntegerField={handleChangeIntegerField}
        />
      </div>

      {/* TODO: Section temporarily hidden as requested in https://fst-jira.dot.app.corpintra.net/browse/CART-18069
      <h3>{t('feedback.billing')}</h3>
      <div className="w-100" data-testid={`feedback-billing-section-${index}`}>
        <Grid container spacing={2} rowSpacing={1} sx={{ marginBottom: '16px' }} columnSpacing={1}>
          <Grid item xs={12} sm={6} md={3}>
            <InputText
              id={`input-campaign-costCenter-feedback-${index}`}
              fieldPath={[FEEDBACK.FEEDBACK, `campaigns[${index}]`, FEEDBACK.BILLING, FEEDBACK.COST_CENTER]}
              noTranslationField
              label={getLabelForPossibleDisabledInput('feedback.costCenter', BILLING_COST_CENTER_FIELD)}
              handleChange={(ev) => handleChangeText(FEEDBACK.COST_CENTER, ev.target.value, FEEDBACK.BILLING)}
              value={formatValue(campaign?.billing?.costCenter)}
              className="w-100"
              disabled={checkIsDisabledField(BILLING_COST_CENTER_FIELD)}
              maxLength={1024}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InputText
              id={`input-campaign-pmContract-feedback-${index}`}
              fieldPath={[FEEDBACK.FEEDBACK, `campaigns[${index}]`, FEEDBACK.BILLING, FEEDBACK.PM_CONTRACT]}
              noTranslationField
              label={getLabelForPossibleDisabledInput('feedback.pmContract', BILLING_PM_CONTRACT_FIELD)}
              handleChange={(ev) => handleChangeText(FEEDBACK.PM_CONTRACT, ev.target.value, FEEDBACK.BILLING)}
              value={formatValue(campaign?.billing?.pmContract)}
              className="w-100"
              disabled={checkIsDisabledField(BILLING_PM_CONTRACT_FIELD)}
              maxLength={1024}
            />
          </Grid>
          {!isVANSubCR
            && (
              <Grid item xs={12} sm={6} md={3}>
                <InputText
                  id={`input-campaign-coNumber-feedback-${index}`}
                  fieldPath={[FEEDBACK.FEEDBACK, `campaigns[${index}]`, FEEDBACK.BILLING, FEEDBACK.CO_NUMBER]}
                  noTranslationField
                  maxLength={1024}
                  label={getLabelForPossibleDisabledInput('feedback.coNumber', BILLING_CO_NUMBER_FIELD)}
                  handleChange={(ev) => handleChangeText(FEEDBACK.CO_NUMBER, ev.target.value, FEEDBACK.BILLING)}
                  value={formatValue(campaign?.billing?.coNumber)}
                  className="w-100"
                  disabled={checkIsDisabledField(BILLING_CO_NUMBER_FIELD)}
                />
              </Grid>
            )}
        </Grid>
      </div> */}
    </>
  );
};

const mapStateToProps = (state) => ({
  subCampaignFeedback: getSubCampaignFeedback(state),
  isVANSubCR: getIsVANSubCR(state),
});

export default (connect(mapStateToProps)(TimeBillingSection));
